import { db } from './config';
import { 
    collection, 
    doc, 
    getDocs, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    where,
    onSnapshot
} from 'firebase/firestore';

/**
 * Formatea la fecha actual al estilo estándar en español:
 * "13 de agosto de 2026 a las 4:36:03 p.m. UTC-3"
 */
const getFechaLegible = () => {
    const ahora = new Date();
    const fechaTexto = new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'long',
        timeStyle: 'medium',
        timeZone: 'America/Argentina/Buenos_Aires'
    }).format(ahora);

    return `${fechaTexto} UTC-3`;
};

/**
 * Limpia recursivamente objetos eliminando propiedades undefined o nulas
 */
const cleanPayload = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const cleaned = {};
    Object.entries(obj).forEach(([key, val]) => {
        if (val === undefined || val === null || val === '') return;
        if (typeof val === 'object' && !Array.isArray(val) && !(val instanceof Date)) {
            cleaned[key] = cleanPayload(val);
        } else {
            cleaned[key] = val;
        }
    });
    return cleaned;
};

// ==========================================
// GESTIÓN DE TABLAS MAESTRAS (ABM)
// ==========================================

export const getMaestroEntidades = async (coleccionNombre) => {
    try {
        const ref = collection(db, `public/data/${coleccionNombre}`);
        const snap = await getDocs(ref);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        return list.sort((a, b) => {
            const nameA = (a.nombre || a.razonSocial || '').toLowerCase();
            const nameB = (b.nombre || b.razonSocial || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    } catch (error) {
        console.error(`Error al recuperar maestro ${coleccionNombre}:`, error);
        throw error;
    }
};

export const saveMaestroEntidad = async (coleccionNombre, entityData, id = null) => {
    try {
        const payload = cleanPayload(entityData);
        payload.ultimaModificacion = getFechaLegible();

        if (id) {
            const docRef = doc(db, `public/data/${coleccionNombre}`, id);
            await updateDoc(docRef, payload);
            return id;
        } else {
            payload.fechaAlta = getFechaLegible();
            const colRef = collection(db, `public/data/${coleccionNombre}`);
            const newDoc = await addDoc(colRef, payload);
            return newDoc.id;
        }
    } catch (error) {
        console.error(`Error al guardar en ${coleccionNombre}:`, error);
        throw error;
    }
};

export const deleteMaestroEntidad = async (coleccionNombre, id) => {
    try {
        await deleteDoc(doc(db, `public/data/${coleccionNombre}`, id));
    } catch (error) {
        console.error(`Error al eliminar en ${coleccionNombre}:`, error);
        throw error;
    }
};

// ==========================================
// GESTIÓN DE FACTURAS EMITIDAS (INGRESOS)
// ==========================================

export const saveFacturaEmitida = async (facturaData, id = null) => {
    try {
        const payload = cleanPayload(facturaData);
        payload.ultimaModificacion = getFechaLegible();

        if (id) {
            const docRef = doc(db, 'public/data/facturacion_emitida', id);
            await updateDoc(docRef, payload);
            return id;
        } else {
            payload.fechaCarga = getFechaLegible();
            const colRef = collection(db, 'public/data/facturacion_emitida');
            const newDoc = await addDoc(colRef, payload);
            return newDoc.id;
        }
    } catch (error) {
        console.error("Error al guardar factura emitida:", error);
        throw error;
    }
};

export const getFacturasEmitidas = async (periodo = null) => {
    try {
        let q = collection(db, 'public/data/facturacion_emitida');
        if (periodo) {
            q = query(q, where('periodo', '==', periodo));
        }
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        return list.sort((a, b) => (b.fechaEmision || '').localeCompare(a.fechaEmision || ''));
    } catch (error) {
        console.error("Error al consultar facturas emitidas:", error);
        throw error;
    }
};

/**
 * Suscripción en tiempo real a Facturas Emitidas
 */
export const subscribeFacturasEmitidas = (callback, periodo = null) => {
    let q = collection(db, 'public/data/facturacion_emitida');
    if (periodo) {
        q = query(q, where('periodo', '==', periodo));
    }

    return onSnapshot(q, (snapshot) => {
        const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const sortedList = list.sort((a, b) => (b.fechaEmision || '').localeCompare(a.fechaEmision || ''));
        callback(sortedList);
    }, (error) => {
        console.error("Error al suscribirse a facturas emitidas:", error);
    });
};

export const deleteFacturaEmitida = async (id) => {
    try {
        await deleteDoc(doc(db, 'public/data/facturacion_emitida', id));
    } catch (error) {
        console.error("Error al eliminar factura emitida:", error);
        throw error;
    }
};

// ==========================================
// GESTIÓN DE GASTOS Y COMPRAS (EGRESOS)
// ==========================================

export const saveGastoCompra = async (gastoData, id = null) => {
    try {
        const payload = cleanPayload(gastoData);
        payload.ultimaModificacion = getFechaLegible();

        if (id) {
            const docRef = doc(db, 'public/data/gastos_compras', id);
            await updateDoc(docRef, payload);
            return id;
        } else {
            payload.fechaCarga = getFechaLegible();
            const colRef = collection(db, 'public/data/gastos_compras');
            const newDoc = await addDoc(colRef, payload);
            return newDoc.id;
        }
    } catch (error) {
        console.error("Error al guardar gasto/compra:", error);
        throw error;
    }
};

export const getGastosCompras = async (periodo = null) => {
    try {
        let q = collection(db, 'public/data/gastos_compras');
        if (periodo) {
            q = query(q, where('periodo', '==', periodo));
        }
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        return list.sort((a, b) => (b.fechaGasto || '').localeCompare(a.fechaGasto || ''));
    } catch (error) {
        console.error("Error al consultar gastos/compras:", error);
        throw error;
    }
};

/**
 * Suscripción en tiempo real a Gastos y Compras
 */
export const subscribeGastosCompras = (callback, periodo = null) => {
    let q = collection(db, 'public/data/gastos_compras');
    if (periodo) {
        q = query(q, where('periodo', '==', periodo));
    }

    return onSnapshot(q, (snapshot) => {
        const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const sortedList = list.sort((a, b) => (b.fechaGasto || '').localeCompare(a.fechaGasto || ''));
        callback(sortedList);
    }, (error) => {
        console.error("Error al suscribirse a gastos y compras:", error);
    });
};

export const deleteGastoCompra = async (id) => {
    try {
        await deleteDoc(doc(db, 'public/data/gastos_compras', id));
    } catch (error) {
        console.error("Error al eliminar gasto:", error);
        throw error;
    }
};